import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Help as HelpIcon } from "@mui/icons-material";
import { apiClient } from "../../../utils/apiClient";

interface VariableDocEntry {
  path: string;
  description: string;
  type: string;
  exampleValue?: string;
}

/**
 * Shared component that shows variable syntax help for template editors.
 * Fetches variable documentation from the backend (derived from the Zod schema).
 */
export function VariableSyntaxHelp() {
  const [variables, setVariables] = useState<VariableDocEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiClient.get<{ variables: VariableDocEntry[] }>(
          `/admin/email-automation/variables/list`
        );
        if (!cancelled) setVariables(res.variables);
      } catch (err) {
        console.error("Error loading variable docs:", err);
        if (!cancelled) setError("Failed to load variable reference");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Group entries by top-level path segment
  const grouped = React.useMemo(() => {
    if (!variables) return [];
    const groups: { label: string; entries: VariableDocEntry[] }[] = [];
    const groupMap = new Map<string, VariableDocEntry[]>();

    for (const v of variables) {
      const topLevel = v.path.split(".")[0];
      if (!groupMap.has(topLevel)) {
        groupMap.set(topLevel, []);
      }
      groupMap.get(topLevel)!.push(v);
    }

    groupMap.forEach((entries, label) => {
      // Convert camelCase to title case for display
      const displayLabel = label
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (s: string) => s.toUpperCase())
        .trim();
      groups.push({ label: displayLabel, entries });
    });

    return groups;
  }, [variables]);

  return (
    <Accordion
      sx={{
        "&:before": { display: "none" },
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        mb: 2,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ minHeight: 48, "& .MuiAccordionSummary-content": { my: 1 } }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <HelpIcon fontSize="small" color="action" />
          <Typography variant="subtitle2">Variable Syntax Reference</Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use <code>$stepName.field.subfield</code> syntax to inject data from previous pipeline steps.
          Missing values will show as <code>$stepName.field: undefined</code>.
        </Typography>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        {!variables && !error && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {variables && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Variable</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Example Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {grouped.map((group) => (
                <React.Fragment key={group.label}>
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Chip label={group.label} size="small" sx={{ mt: 1 }} />
                    </TableCell>
                  </TableRow>
                  {group.entries.map((v) => (
                    <TableRow key={v.path}>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        ${v.path}
                      </TableCell>
                      <TableCell>{v.description}</TableCell>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {v.exampleValue || "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
